import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet } from 'react-native';

import { A } from '@expo/html-elements';
import View from '@expo/html-elements/build/primitives/View';
import { theme } from '@garnish/constants';

import { BodyText, TextLink } from '@sg/garnish';

import { SG_SUPPORT_EMAIL } from '../../../../constants';
import { JOIN_OR_SIGN_IN_SCREEN_BODY_MAX_FONT_SIZE_MULTIPLIER } from '../../JoinOrSignInScreen.constants';

// ─────────────────────────────────────────────────────────────────────────────

export const JoinOrSignInVerificationStepFooter = (
  props: JoinOrSignInVerificationStepFooterProps,
) => {
  const { isLoading, resendCode } = props;

  const { formatMessage } = useIntl();
  const mailA11yLabel = formatMessage(messages.mailA11yLabel);

  const maxFontSizeMultiplier = JOIN_OR_SIGN_IN_SCREEN_BODY_MAX_FONT_SIZE_MULTIPLIER;

  return (
    <>
      <View style={styles.footerRow}>
        <BodyText sizeMatch={['14']} maxFontSizeMultiplier={maxFontSizeMultiplier}>
          <FormattedMessage {...messages.dontSeeEmail} />
        </BodyText>

        <TextLink
          sizeMatch={['14']}
          disabled={isLoading}
          onPress={resendCode}
          maxFontSizeMultiplier={maxFontSizeMultiplier}
        >
          <FormattedMessage {...messages.resend} />
        </TextLink>
      </View>

      <View style={styles.footerRow}>
        <BodyText sizeMatch={['14']} maxFontSizeMultiplier={maxFontSizeMultiplier}>
          <FormattedMessage {...messages.contactUs} />
        </BodyText>

        <A href={`mailto:${SG_SUPPORT_EMAIL}`} accessibilityLabel={mailA11yLabel}>
          <BodyText underline sizeMatch={['14']} maxFontSizeMultiplier={maxFontSizeMultiplier}>
            {SG_SUPPORT_EMAIL}
          </BodyText>
        </A>
      </View>
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  dontSeeEmail: {
    defaultMessage: 'Don’t see an email?',
    description: 'Join or Sign In | Don’t see an email',
  },
  resend: {
    defaultMessage: 'Resend',
    description: 'Join or Sign In | Resend',
  },
  contactUs: {
    defaultMessage: 'Having trouble? Contact us at',
    description: 'Join or Sign In | Contact us at',
  },
  mailA11yLabel: {
    defaultMessage: 'Send email to sweetgreen support',
    description: 'Join or Sign In | Email support',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  footerRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing['1'],
    marginBottom: theme.spacing['2'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type JoinOrSignInVerificationStepFooterProps = {
  isLoading: boolean;
  resendCode: () => void;
};
